/**
 * @fileoverview Query key utilities for TanStack Query caching.
 * @module shared/utils/queryKeys
 */

import {
  UnifiedStatisticsFilterDTO,
  StatisticsDimension,
  TimeAggregation,
} from '@/lib/statisticsService'

/**
 * Creates a stable hash key from filter parameters and groupBy dimensions for query caching.
 *
 * @param filter - Filter parameters
 * @param groupBy - Grouping dimensions
 * @param timeAggregation - Time aggregation level
 * @returns Stable string key for caching
 * @example
 * ```typescript
 * const key = createUnifiedFilterKey(
 *   { startDate: '2024-01-01', endDate: '2024-01-31' },
 *   ['COUNTRY', 'PRODUCT'],
 *   'MONTHLY'
 * );
 * ```
 */
export function createUnifiedFilterKey(
  filter: UnifiedStatisticsFilterDTO,
  groupBy: StatisticsDimension[] = ['COUNTRY'],
  timeAggregation?: TimeAggregation
): string {
  const normalized = normalizeUnifiedFilter(filter, groupBy, timeAggregation)
  return JSON.stringify(normalized)
}

/**
 * Normalizes filter parameters to ensure consistent ordering and values.
 *
 * @param filter - Raw filter parameters
 * @param groupBy - Grouping dimensions
 * @param timeAggregation - Time aggregation level
 * @returns Normalized filter object
 */
export function normalizeUnifiedFilter(
  filter: UnifiedStatisticsFilterDTO,
  groupBy: StatisticsDimension[],
  timeAggregation?: TimeAggregation
): Record<string, unknown> {
  const normalized: Record<string, unknown> = {}
  if (filter.startDate) normalized.startDate = filter.startDate
  if (filter.endDate) normalized.endDate = filter.endDate
  if (timeAggregation) normalized.timeAggregation = timeAggregation

  // New unified flags
  if (filter.includeAllocated === true) normalized.includeAllocated = true
  if (filter.allocation?.strategy) normalized['allocation.strategy'] = filter.allocation.strategy

  // Sort arrays for consistent keys
  if (filter.countries?.length) {
    normalized.countries = [...filter.countries].sort()
  }
  if (filter.skus?.length) {
    normalized.skus = [...filter.skus].sort()
  }
  if (filter.tags?.length) {
    normalized.tags = [...filter.tags].sort()
  }
  if (groupBy.includes('PRODUCT')) {
    normalized.group = filter.group ?? 'PRODUCT'
  }

  // Add groupBy dimensions (sorted for consistency)
  normalized.groupBy = [...groupBy].sort()

  return normalized
}

// Legacy function for backward compatibility
export function createFilterKey(filter: UnifiedStatisticsFilterDTO): string {
  return createUnifiedFilterKey(filter)
}

export function normalizeFilter(filter: UnifiedStatisticsFilterDTO): UnifiedStatisticsFilterDTO {
  const normalized: UnifiedStatisticsFilterDTO = {}

  if (filter.startDate) normalized.startDate = filter.startDate
  if (filter.endDate) normalized.endDate = filter.endDate
  if (filter.timeAggregation) normalized.timeAggregation = filter.timeAggregation

  if (filter.countries?.length) {
    normalized.countries = [...filter.countries].sort()
  }
  if (filter.skus?.length) {
    normalized.skus = [...filter.skus].sort()
  }
  if (filter.tags?.length) {
    normalized.tags = [...filter.tags].sort()
  }

  return normalized
}

/**
 * Query key factory for unified statistics API queries.
 */
export const queryKeys = {
  /**
   * Query key for sales statistics with flexible groupBy dimensions.
   *
   * @param filter - Filter parameters
   * @param groupBy - Grouping dimensions
   * @param timeAggregation - Time aggregation level
   * @returns Query key array
   */
  salesStatistics: (
    filter: UnifiedStatisticsFilterDTO,
    groupBy: StatisticsDimension[] = ['COUNTRY'],
    timeAggregation?: TimeAggregation
  ) => ['statistics', 'sales', createUnifiedFilterKey(filter, groupBy, timeAggregation)] as const,

  /**
   * Query key for cancellation statistics with flexible groupBy dimensions.
   *
   * @param filter - Filter parameters
   * @param groupBy - Grouping dimensions
   * @param timeAggregation - Time aggregation level
   * @returns Query key array
   */
  cancellationStatistics: (
    filter: UnifiedStatisticsFilterDTO,
    groupBy: StatisticsDimension[] = ['COUNTRY'],
    timeAggregation?: TimeAggregation
  ) =>
    [
      'statistics',
      'cancellations',
      createUnifiedFilterKey(filter, groupBy, timeAggregation),
    ] as const,

  /**
   * Query key for merged sales + cancellation statistics (mode=BOTH).
   *
   * @param filter - Filter parameters
   * @param groupBy - Grouping dimensions
   * @param timeAggregation - Time aggregation level
   * @returns Query key array
   */
  bothStatistics: (
    filter: UnifiedStatisticsFilterDTO,
    groupBy: StatisticsDimension[] = ['COUNTRY'],
    timeAggregation?: TimeAggregation
  ) => ['statistics', 'both', createUnifiedFilterKey(filter, groupBy, timeAggregation)] as const,

  /**
   * Query key for audit statistics with flexible groupBy dimensions (admin only).
   *
   * @param filter - Filter parameters
   * @param groupBy - Grouping dimensions
   * @param timeAggregation - Time aggregation level
   * @returns Query key array
   */
  auditStatistics: (
    filter: UnifiedStatisticsFilterDTO,
    groupBy: StatisticsDimension[] = ['COUNTRY'],
    timeAggregation?: TimeAggregation
  ) => ['statistics', 'audit', createUnifiedFilterKey(filter, groupBy, timeAggregation)] as const,

  /**
   * Query key for all variants list.
   *
   * @returns Query key array
   */
  variants: () => ['variants'] as const,

  /** @deprecated Use variants() instead */
  skus: () => ['variants'] as const,

  /**
   * Query key for variant images data.
   *
   * @returns Query key array
   */
  variantImages: () => ['variants', 'images'] as const,

  /** @deprecated Use variantImages() instead */
  skuImages: () => ['variants', 'images'] as const,

  // Legacy query keys for backward compatibility during migration
  /**
   * @deprecated Use salesStatistics with ['TIME'] groupBy instead
   */
  ecommerce: (filter: UnifiedStatisticsFilterDTO) =>
    queryKeys.salesStatistics(filter, ['TIME'], filter.timeAggregation || 'MONTHLY'),

  /**
   * @deprecated Use salesStatistics with ['COUNTRY', 'PRODUCT'] groupBy instead
   */
  productsByCountry: (filter: UnifiedStatisticsFilterDTO) =>
    queryKeys.salesStatistics(filter, ['COUNTRY', 'PRODUCT']),
} as const

/**
 * Prefix a query key with the active organization to scope caches per-tenant.
 *
 * @param activeOrgId - Active organization id
 * @param key - Existing query key
 * @returns Prefixed key array including the org id
 */
export function prefixWithOrg<T extends readonly unknown[]>(
  activeOrgId: string,
  key: T
): readonly unknown[] {
  return ['org', activeOrgId, ...key]
}
